import { motion } from "framer-motion";
import SectionLabel from "../SectionLabel";
import { TESTIMONIALS } from "../../data/testimonials";

/**
 * Editorial pull-quote band — three anonymised client voices presented
 * as a luxury magazine spread. No card chrome, no review widget noise:
 * a serif opening quote mark anchors each block, italic Cormorant
 * Garamond renders the quote itself, and a hairline divider separates
 * the body from the small tracked attribution beneath.
 */
export default function Testimonials() {
    return (
        <section
            className="relative section-pad bg-[#050608] border-t border-[rgba(201,168,106,0.10)] overflow-hidden"
            data-testid="testimonials-section"
        >
            <div className="shell">
                <div className="flex flex-col items-center text-center mb-14 md:mb-20">
                    <SectionLabel
                        number="02"
                        label="Client Voices"
                        align="center"
                    />
                    <h2
                        className="mt-8 font-serif text-4xl md:text-5xl lg:text-6xl max-w-3xl leading-[1.08]"
                        data-testid="testimonials-heading"
                    >
                        {"What our clients say, "}
                        <span className="italic text-[#C9A86A]">
                            in their own words.
                        </span>
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                    {TESTIMONIALS.map((t, idx) => (
                        <motion.figure
                            key={t.attribution}
                            suppressHydrationWarning
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.25 }}
                            transition={{
                                duration: 0.8,
                                delay: idx * 0.12,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="relative px-6 md:px-2 py-10 md:py-12 flex flex-col"
                            data-testid={`testimonial-card-${idx}`}
                        >
                            {/* Decorative serif quote mark */}
                            <span
                                aria-hidden="true"
                                className="absolute -top-2 md:-top-3 left-4 md:left-0 font-serif text-7xl md:text-8xl leading-none text-[#C9A86A]/40 select-none"
                            >
                                &ldquo;
                            </span>

                            <blockquote className="relative pt-8 md:pt-10 flex-1">
                                <p className="font-serif italic font-light text-lg md:text-xl text-white/80 leading-relaxed">
                                    {t.quote}
                                </p>
                            </blockquote>

                            <div className="hairline-solid mt-8 md:mt-10" />

                            <figcaption className="mt-5 text-xs tracking-[0.18em] uppercase text-white/50">
                                {t.attribution}
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>
            </div>
        </section>
    );
}
